import { useState, useEffect, useCallback, useMemo } from "react";
import isAuthenticated from "../../../../components/Utils/isAuthenticated";
import {
	Snackbar,
	Checkbox,
	FormControlLabel,
	Button,
	ButtonGroup,
	CircularProgress,
} from "@mui/material";
import ListSelect from "../../../../components/Utils/ListSelect";
import RefreshIcon from "@mui/icons-material/Refresh";
import { roles, rolesDescription } from "../../../../components/Utils/Descriptions";
import { useMutation, useQueryClient } from "react-query";
import ClassAdjustCombineDialog from "./ClassAdjustCombineDialog";
import { deleteAdjustedClassId } from "../../../../components/Api/Deletes";
import UndoCombineDialog from "./undoCombineDialog";
import ConfirmEvaluationDialog from "./ConfirmEvaluationDialog";
import { postBulkBlueCourses } from "../../../../components/Api/Post";
import SubmitAllSuitableCoursesDialog from "./SubmitAllSuitableCoursesDialog";
import ConfirmCancelDialog from "./ConfirmCancelDialog";
import InstructorCell from "./InstructorCell";
import DataGridTable from "./DataGridTable";
import ClassPanel from "./ClassPanel";
import InstancedClassAdjustModal from "./InstancedClassAdjustModal";
import dayjs from "dayjs";
import PostBatchSubmitDialog from "./PostBatchSubmitDialog";
import generateReceiptPdf from "./generateReceiptPdf";
import displayBlockPDF from "./displayBlockPDF";
import MixedRoleSubmitDialog from "./MixedRoleSubmitDialog";
import {
	getMixedTARoleCourses,
	normalizeRoleId,
	removeRoleFromCourse,
} from "./mixedRoleSafeguards";
import { useAtom, useAtomValue } from "jotai";
import {
	selectedClassAtom,
	termsAtom,
	selectedDeptAtom,
	unsavedChangesAtom,
	combiningClassesArrayAtom,
	spotOrderingSelectionModelAtom,
} from "../../../../components/Atoms/atoms";
import LaunchIcon from "@mui/icons-material/Launch";

// Import the new context hook
import { useSPOTOrdering } from "../../../../context";
import SelectedClassModal from "./SelectedClassModal";
import { createEmptyRowSelectionModel, getSelectedIdsFromModel } from "./selectionModel";

function buildPrimaryInstructorFields(roleList = []) {
	const primaryRole =
		roles.map((roleId) => roleList.find((role) => role.class_role === roleId)).find(Boolean) ||
		roleList[0] ||
		null;

	return {
		name: primaryRole?.name || "",
		empid: primaryRole?.empid || "",
		class_role: primaryRole?.class_role || "",
	};
}

function buildInstructorTeamFields(roleList = []) {
	return {
		...buildPrimaryInstructorFields(roleList),
		teamsize: roleList.length,
		teamName: roleList.map((role) => role.name).join(", "),
	};
}

function buildManualRoleForRow(instructor, classRole, row) {
	const [lastName = "", firstName = ""] = String(instructor?.name || "")
		.split(",")
		.map((part) => part.trim());

	return {
		ccid: instructor.ccid,
		class_role: classRole,
		checked: false,
		classid: row.classid,
		disabled: false,
		empid: instructor.empid,
		name: instructor.name,
		first_name: firstName,
		last_name: lastName,
		role_description: rolesDescription.find((role) => role.id === classRole)?.name ?? classRole,
		roleid: Number(instructor.empid) + row.classid,
		term: row.term,
		import_status: "MANUAL",
	};
}

function hasPreExcludeState(course) {
	return course?.preExcludeEvaluate !== undefined && course?.preExcludeEvaluate !== null;
}

function buildToggleExcludeUpdates(course, shouldExclude) {
	if (!course) {
		return null;
	}

	if (shouldExclude) {
		return {
			evaluate: "No",
			form: "EXCL",
			wasExcluded: Boolean(course.wasExcluded),
			wasEvaluated: course.evaluate === "Yes",
			preExcludeEvaluate: course.evaluate ?? "",
			preExcludeForm: course.form && course.form !== "EXCL" ? course.form : "",
		};
	}

	const restorePreviousState = hasPreExcludeState(course);
	const restoredEvaluate = restorePreviousState ? course.preExcludeEvaluate : "";
	const restoredForm = restorePreviousState ? (course.preExcludeForm ?? "") : "";

	return {
		evaluate: restoredEvaluate,
		form: restoredForm,
		wasExcluded: restorePreviousState ? Boolean(course.wasExcluded) : true,
		wasEvaluated: restoredEvaluate === "Yes",
		preExcludeEvaluate: null,
		preExcludeForm: null,
	};
}

export default function ClassAdjustTable(props) {
	const courseStatusFilters = ["All", "Submitted", "Not Submitted"];

	const {
		batchUpdated,
		setBatchUpdated,
		disabledUI,
		setDisabledUI,
		blockIdsByDept,
		fetchedClassIdsLoading,
		updateClassMutation,
		isAdmin,
		isTermActive,
		departmentsList,
		handleDepartmentChange,
		handleRefresh,
		loading,
		allBlockItems,
		handleChangeBlkId,
		allBlockItemsTA,
		openSelectedClassModal,
		setOpenSelectedClassModal,
	} = props;

	const {
		rows,
		effectiveRows,
		displayRows,
		updateRow,
		updateRowsBatch,
		applySearch,
		searchTerm,
		setRows,
	} = useSPOTOrdering();

	const auth = isAuthenticated();
	const token = auth.token;
	const ccid = auth.ccid;
	const [selectedClass, setSelectedClass] = useAtom(selectedClassAtom);
	const [unsavedChanges, setUnsavedChanges] = useAtom(unsavedChangesAtom);
	const [, setCombiningClassesArray] = useAtom(combiningClassesArrayAtom);
	const selectedDept = useAtomValue(selectedDeptAtom);
	const [terms, setTerms] = useAtom(termsAtom);
	const queryClient = useQueryClient();
	const [dateFormat, setDateFormat] = useState("DD/MM/YY");
	const [downloadReceipt, setDownloadReceipt] = useState(false);
	const [, setHasCombinedCourse] = useState(false);

	const [selectionModel, setSelectionModel] = useAtom(spotOrderingSelectionModelAtom);

	// Local UI state
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [addInstructor, setAddInstructor] = useState(false);
	const [openClassAdjustCombineDialog, setOpenClassAdjustCombineDialog] = useState(false);
	const [undoDialogOpen, setUndoDialogOpen] = useState(false);
	const [classesForCombining, setClassesForCombining] = useState([]);
	const [teamTeachingAgree, setTeamTeachingAgree] = useState(false);
	const [unwantedCombination, setUnwantedCombination] = useState(false);
	const [evaluationDialogState, setEvaluationDialogState] = useState("");
	const [openEvaluationDialog, setOpenEvaluationDialog] = useState(false);
	const [classForEvaluatedDialog, setClassForEvaluatedDialog] = useState(null);
	const [, setClickedAddNotAllowed] = useState(false);
	const [listOfSuitableCourses, setListOfSuitableCourses] = useState([]);
	const [openSubmitAllSuitableCoursesDialog, setOpenSubmitAllSuitableCoursesDialog] =
		useState(false);
	const [submitAllLoading, setSubmitAllLoading] = useState(false);
	const [filterKey, setFilterKey] = useState(0);
	const [reviewingClasses, setReviewingClasses] = useState(false);
	const [prevRowsState, setPrevRowsState] = useState([]);
	const [openConfirmCancelDialog, setOpenConfirmCancelDialog] = useState(false);
	const [confirmSubmitAllSelected, setConfirmSubmitAllSelected] = useState(false);
	const [openAddInstancedClass, setOpenAddInstancedClass] = useState(false);
	const [submitTAOnly, setSubmitTAOnly] = useState(false);
	const [courseStatusFilter, setCourseStatusFilter] = useState("All");
	const [tabValue, setTabValue] = useState(0);
	const [mixedRoleSubmitDialog, setMixedRoleSubmitDialog] = useState({
		open: false,
		source: null,
		courses: [],
	});

	const [openBulkEdit, setOpenBulkEdit] = useState(false);

	const effectiveRowsById = useMemo(
		() => new Map(effectiveRows.map((row) => [row.classid, row])),
		[effectiveRows],
	);
	const filteredDisplayRows = useMemo(() => {
		if (courseStatusFilter === "Submitted") {
			return displayRows.filter((row) => row._hasBlueCourseEntry);
		}

		if (courseStatusFilter === "Not Submitted") {
			return displayRows.filter((row) => !row._hasBlueCourseEntry);
		}

		return displayRows;
	}, [courseStatusFilter, displayRows]);
	const selectedBulkClassIds = useMemo(
		() => getSelectedIdsFromModel(selectionModel, filteredDisplayRows),
		[selectionModel, filteredDisplayRows],
	);
	const hasBulkSelection = selectedBulkClassIds.length > 0;

	// Computed values that depend on rows
	const someNotTA = selectedBulkClassIds.some((classid) => {
		const row = effectiveRowsById.get(classid);
		return row?.roles?.some((role) => role.class_role !== "TA");
	});

	const someAreTA = selectedBulkClassIds.some((classid) => {
		const row = effectiveRowsById.get(classid);
		return row?.roles?.some((role) => role.class_role === "TA");
	});

	const allCoursesNonLEC = selectedBulkClassIds.every((classid) => {
		const row = effectiveRowsById.get(classid);
		return row?.ctype !== "LEC";
	});

	// Load saved preferences
	useEffect(() => {
		const saved = localStorage.getItem("downloadReceipt");
		if (saved === "Y") {
			setDownloadReceipt(true);
		}
	}, []);

	// === MIGRATED: Clear pending when checkbox selection changes ===
	useEffect(() => {
		// Clear "Pending" from rows not in selection
		const updates = rows
			.filter((r) => r.evaluate === "Pending" && !selectedBulkClassIds.includes(r.classid))
			.map((r) => ({ classid: r.classid, updates: { evaluate: "" } }));

		if (updates.length > 0) {
			updateRowsBatch(updates);
		}
	}, [rows, selectedBulkClassIds, updateRowsBatch]);

	// Delete class mutation
	const deleteClassMutation = useMutation(
		async ({ token, classid }) => {
			await deleteAdjustedClassId({ token, classid });
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries([
					"blueCoursesObjectsByDeptAndTerm",
					selectedDept.deptId,
					terms.termNumber,
				]);
				queryClient.invalidateQueries([
					"classIdObjectsByDeptAndTerm",
					selectedDept.deptId,
					terms.termNumber,
				]);
			},
			onError: (error) => {
				console.error("Failed to delete class:", error);
			},
		},
	);

	// === MIGRATED: handleSearch uses applySearch from context ===
	const handleSearch = useCallback(
		(e) => {
			applySearch(e.target.value);
		},
		[applySearch],
	);

	// Handle role change for instructor
	const handleChangeRole = (e, instructor, row) => {
		const role = e.target.value;

		// Clone roles array and update the specific role
		const updatedRoles = [...row.roles];
		const roleIndex = updatedRoles.findIndex((r) => r.name === instructor.name);

		const wasAllNonTA = updatedRoles.every((r) => r.class_role !== "TA");
		const wasAllTA = updatedRoles.every((r) => r.class_role === "TA");

		updatedRoles[roleIndex] = { ...updatedRoles[roleIndex], class_role: role };

		// Determine if form should be cleared
		let formUpdate = {};
		if ((wasAllNonTA && role === "TA") || (wasAllTA && role !== "TA")) {
			formUpdate = { form: null };
		}

		updateRow(row.classid, { roles: updatedRoles, ...formUpdate });
		setUnsavedChanges(true);
	};

	const handleCloseSnackbar = (event, reason) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarOpen(false);
	};

	// === DATE HANDLERS - using updateRow ===
	const handleDateChangeEndDate = (date) => {
		const formattedDate = date.format("YYYY-MM-DD");

		setSelectedClass((prev) => ({
			...prev,
			c_edate: formattedDate,
		}));

		updateRow(selectedClass.classid, { c_edate: formattedDate });
		setUnsavedChanges(true);
	};

	const handleDateChangeSurveyStart = (date) => {
		let surveyEnd = dayjs(date).add(14, "days");

		if (
			dayjs(selectedClass.examdate).isBefore(surveyEnd) ||
			dayjs(selectedClass.examdate).isSame(surveyEnd)
		) {
			surveyEnd = dayjs(selectedClass.examdate).subtract(1, "day");
		}

		const updates = {
			survey_start: date.format("YYYY-MM-DD"),
			survey_end: surveyEnd.format("YYYY-MM-DD"),
		};

		setSelectedClass((prev) => ({ ...prev, ...updates }));
		updateRow(selectedClass.classid, updates);
		setUnsavedChanges(true);
	};

	const handleDateChangeSurveyEnd = (date) => {
		const formattedDate = date.format("YYYY-MM-DD");

		setSelectedClass((prev) => ({
			...prev,
			survey_end: formattedDate,
		}));

		updateRow(selectedClass.classid, { survey_end: formattedDate });
		setUnsavedChanges(true);
	};

	const handleDateChangeExamDate = (date) => {
		const defaultSurveyStart = dayjs(selectedClass.c_edate).subtract(14, "days");
		const defaultSurveyEnd = dayjs(selectedClass.c_edate).add(2, "days");
		const adjustedSurveyStart = dayjs(date).subtract(15, "days");
		const adjustedSurveyEnd = dayjs(date).subtract(1, "days");

		let surveyStart, surveyEnd;

		if (
			dayjs(date).isBetween(defaultSurveyStart, defaultSurveyEnd, null, "[]") ||
			dayjs(date).isBefore(defaultSurveyStart)
		) {
			surveyStart = adjustedSurveyStart;
			surveyEnd = adjustedSurveyEnd;
		} else {
			surveyStart = defaultSurveyStart;
			surveyEnd = defaultSurveyEnd;
		}

		const updates = {
			examdate: date.format("YYYY-MM-DD"),
			survey_start: surveyStart.format("YYYY-MM-DD"),
			survey_end: surveyEnd.format("YYYY-MM-DD"),
		};

		setSelectedClass((prev) => ({ ...prev, ...updates }));
		updateRow(selectedClass.classid, updates);
		setUnsavedChanges(true);
	};

	const handleSaveChanges = async (options = {}) => {
		const skipMixedRoleCheck = Boolean(options?.skipMixedRoleCheck);
		let classData = selectedClass;

		if (!skipMixedRoleCheck) {
			const mixedRoleCourses = getMixedTARoleCourses([classData]);
			if (mixedRoleCourses.length > 0) {
				setMixedRoleSubmitDialog({
					open: true,
					source: "single",
					courses: mixedRoleCourses,
				});
				return;
			}
		}

		if (classData.evaluate !== "No" && !classData.wasExcluded) {
			const chosenBlockId = blockIdsByDept.find((blockId) => blockId.blkid === selectedClass.form);
			const roleOfBlockId = chosenBlockId?.inst_role;
			if (classData.roles.some((role) => role.class_role !== "TA") && roleOfBlockId === "TA") {
				alert(
					"Cannot submit course. Question block is meant for a TA but there are non-TA instructors assigned to the course.",
				);
				return;
			}
		}

		if (!selectedClass.evaluate) {
			classData = { ...selectedClass, evaluate: "Yes" };
			await updateClassMutation.mutateAsync({
				classData,
				ccid,
			});
		} else if (selectedClass.evaluate === "No") {
			classData = { ...selectedClass, form: "EXCL" };
			await updateClassMutation.mutateAsync({
				classData,
				ccid,
			});
		} else {
			await updateClassMutation.mutateAsync({
				classData,
				ccid,
			});
		}

		setUnsavedChanges(false);
		setSelectedClass(null);
		setHasCombinedCourse(false);
		if (downloadReceipt) {
			generateReceiptPdf([classData]);
		}
	};

	const handleUndo = async () => {
		const classid = selectedClass.classid;
		await deleteClassMutation.mutateAsync({ token, classid });
		setUndoDialogOpen(false);
		setSelectedClass(null);
	};

	const handleToggleExclude = (value) => {
		if (!selectedClass) {
			return;
		}

		const isCurrentlyExcluded = selectedClass.evaluate === "No";
		if (value === isCurrentlyExcluded) {
			return;
		}

		const updates = buildToggleExcludeUpdates(selectedClass, value);
		if (!updates) {
			return;
		}

		setSelectedClass((prev) => ({
			...prev,
			...updates,
		}));
		updateRow(selectedClass.classid, updates);
		setUnsavedChanges(true);
	};

	const deleteInstructor = (event, instructor) => {
		const currentRow = rows.find((r) => r.classid === selectedClass.classid);
		const updatedRoles = currentRow.roles.filter((role) => role.empid !== instructor.empid);

		updateRow(selectedClass.classid, {
			roles: updatedRoles,
			...buildInstructorTeamFields(updatedRoles),
		});

		setSelectedClass((prev) => ({
			...prev,
			roles: updatedRoles,
			...buildInstructorTeamFields(updatedRoles),
		}));
		setUnsavedChanges(true);
	};

	const handleBulkAddInstructorToSelection = useCallback(
		(instructorToAdd, classRole) => {
			const updates = [];
			let appliedCount = 0;
			let skippedCount = 0;

			selectedBulkClassIds.forEach((classid) => {
				const row = effectiveRowsById.get(classid);
				if (!row) {
					return;
				}

				const currentRoles = Array.isArray(row.roles) ? row.roles : [];
				const alreadyAssigned = currentRoles.some(
					(role) => Number(role.empid) === Number(instructorToAdd.empid),
				);

				if (alreadyAssigned) {
					skippedCount += 1;
					return;
				}

				const updatedRoles = [
					...currentRoles,
					buildManualRoleForRow(instructorToAdd, classRole, row),
				];

				updates.push({
					classid,
					updates: {
						roles: updatedRoles,
						...buildInstructorTeamFields(updatedRoles),
					},
				});
				appliedCount += 1;
			});

			if (updates.length === 0) {
				setSnackbarMessage("Selected instructor is already assigned to every selected course.");
				setSnackbarOpen(true);
				return;
			}

			updateRowsBatch(updates);
			setBatchUpdated(true);
			setUnsavedChanges(true);
			setSnackbarMessage(
				`Added instructor to ${appliedCount} selected course${appliedCount === 1 ? "" : "s"}${
					skippedCount > 0
						? `; skipped ${skippedCount} course${skippedCount === 1 ? "" : "s"} already containing that instructor`
						: ""
				}`,
			);
			setSnackbarOpen(true);
		},
		[effectiveRowsById, selectedBulkClassIds, setBatchUpdated, setUnsavedChanges, updateRowsBatch],
	);

	const handleBulkRemoveInstructorFromSelection = useCallback(
		(assignment) => {
			const updates = [];
			let removedFromCount = 0;

			selectedBulkClassIds.forEach((classid) => {
				const row = effectiveRowsById.get(classid);
				if (!row) {
					return;
				}

				const currentRoles = Array.isArray(row.roles) ? row.roles : [];
				const updatedRoles = currentRoles.filter(
					(role) =>
						!(
							Number(role.empid) === Number(assignment.empid) &&
							role.class_role === assignment.classRole
						),
				);

				if (updatedRoles.length === currentRoles.length) {
					return;
				}

				updates.push({
					classid,
					updates: {
						roles: updatedRoles,
						...buildInstructorTeamFields(updatedRoles),
					},
				});
				removedFromCount += 1;
			});

			if (updates.length === 0) {
				return;
			}

			updateRowsBatch(updates);
			setBatchUpdated(true);
			setUnsavedChanges(true);
			setSnackbarMessage(
				`Removed ${assignment.name} (${assignment.classRole}) from ${removedFromCount} selected course${
					removedFromCount === 1 ? "" : "s"
				}.`,
			);
			setSnackbarOpen(true);
		},
		[effectiveRowsById, selectedBulkClassIds, setBatchUpdated, setUnsavedChanges, updateRowsBatch],
	);

	const applyRoleRemovalToCourses = useCallback(
		(coursesToUpdate = [], roleIdToRemove) => {
			const normalizedRoleIdToRemove = normalizeRoleId(roleIdToRemove);
			const updates = [];

			coursesToUpdate.forEach((course) => {
				const currentRoles = Array.isArray(course?.roles) ? course.roles : [];
				const updatedRoles = removeRoleFromCourse(course, normalizedRoleIdToRemove);

				if (updatedRoles.length === currentRoles.length) {
					return;
				}

				updates.push({
					classid: course.classid,
					updates: {
						roles: updatedRoles,
						...buildInstructorTeamFields(updatedRoles),
					},
				});
			});

			if (updates.length === 0) {
				return 0;
			}

			updateRowsBatch(updates);

			if (selectedClass) {
				const selectedClassUpdate = updates.find(
					(update) => update.classid === selectedClass.classid,
				);

				if (selectedClassUpdate) {
					setSelectedClass((prev) =>
						prev ? { ...prev, ...selectedClassUpdate.updates } : prev,
					);
				}
			}

			setListOfSuitableCourses((prev) =>
				prev.map((course) => {
					const courseUpdate = updates.find((update) => update.classid === course.classid);
					return courseUpdate ? { ...course, ...courseUpdate.updates } : course;
				}),
			);

			setBatchUpdated(true);
			setUnsavedChanges(true);
			setSnackbarMessage(
				`Removed all ${normalizedRoleIdToRemove} roles from ${updates.length} course${
					updates.length === 1 ? "" : "s"
				}. Review before submitting.`,
			);
			setSnackbarOpen(true);

			return updates.length;
		},
		[
			selectedClass,
			setBatchUpdated,
			setSelectedClass,
			setUnsavedChanges,
			updateRowsBatch,
		],
	);

	const handleBulkRemoveRoleFromSelection = useCallback(
		(roleSummary) => {
			const selectedCourses = selectedBulkClassIds
				.map((classid) => effectiveRowsById.get(classid))
				.filter(Boolean);

			applyRoleRemovalToCourses(selectedCourses, roleSummary.roleId);
		},
		[applyRoleRemovalToCourses, effectiveRowsById, selectedBulkClassIds],
	);

	const renderInstructorNameTable = (params, isConstrainedLayout) => {
		
		return <InstructorCell row={params.row} isConstrainedLayout={isConstrainedLayout} />;
	};

	useEffect(() => {
		if (selectedBulkClassIds.length > 0) {
			setSelectedClass(null);
		}
	}, [selectedBulkClassIds, setSelectedClass]);

	useEffect(() => {
		setSelectionModel(createEmptyRowSelectionModel());
		setSelectedClass(null);
	}, [courseStatusFilter, setSelectedClass, setSelectionModel]);

	const handleEvaluatedDialogConfirmation = async (classForConfirm, state) => {
		const classid = classForConfirm.classid;

		if (state === "evaluated" || state === "excluded") {
			await deleteClassMutation.mutateAsync({ token, classid });
			updateRow(classid, { evaluate: "" });
			setSelectedClass(null);
			setOpenSelectedClassModal(false);
		}

		setOpenEvaluationDialog(false);
	};

	const getRowClassName = useCallback((params) => {
		return `super-app-theme--${
			params.row.evaluate === "Yes"
				? "evaluation"
				: params.row.evaluate === "No"
					? "noEvaluation"
					: params.row.evaluate === 'Caution'
						? "pending"
						: ""
		}`;
	}, []);

	const [openPostBatchSubmitDialog, setOpenPostBatchSubmitDialog] = useState(false);
	const [batchSubmitResponse, setBatchSubmitResponse] = useState(null);

	const postAllSuitableClassesMutation = useMutation(
		async ({ token, classesToSubmit, ccid }) => {
			const response = await postBulkBlueCourses({
				token,
				classesToSubmit,
				ccid,
			});
			setBatchSubmitResponse(response);
			setOpenPostBatchSubmitDialog(true);
		},
		{
			onSuccess: () => {
				// Reset all state
				setSnackbarOpen(false);
				setSnackbarMessage("");
				setAddInstructor(false);
				setOpenClassAdjustCombineDialog(false);
				setUndoDialogOpen(false);
				setClassesForCombining([]);
				setTeamTeachingAgree(false);
				setUnwantedCombination(false);
				setEvaluationDialogState("");
				setOpenEvaluationDialog(false);
				setClassForEvaluatedDialog(null);
				setClickedAddNotAllowed(false);
				setListOfSuitableCourses([]);
				setOpenSubmitAllSuitableCoursesDialog(false);
				setSubmitAllLoading(false);
				setReviewingClasses(false);
				setPrevRowsState([]);
				setOpenConfirmCancelDialog(false);
				setConfirmSubmitAllSelected(false);
				setOpenAddInstancedClass(false);
				setHasCombinedCourse(false);

				setFilterKey((prev) => prev + 1);
				setSelectionModel(createEmptyRowSelectionModel());

				queryClient.invalidateQueries([
					"blueCoursesObjectsByDeptAndTerm",
					selectedDept.deptId,
					terms.termNumber,
				]);
				queryClient.invalidateQueries([
					"classIdObjectsByDeptAndTerm",
					selectedDept.deptId,
					terms.termNumber,
				]);
				setUnsavedChanges(false);
			},
			onError: (error) => {
				setSubmitAllLoading(false);
				setReviewingClasses(false);
				setListOfSuitableCourses([]);
				alert("Error submitting classes. Please contact TSS by phone or email.");
				console.error("Failed to update class:", error);
			},
		},
	);

	const handleReviewAllSelected = async () => {
		setConfirmSubmitAllSelected(true);
	};

	const handleCancelSubmitAllSelected = () => {
		setConfirmSubmitAllSelected(false);
	};

	const handleClearSelection = () => {
		if (batchUpdated) {
			setOpenConfirmCancelDialog(true);
		} else {
			setConfirmSubmitAllSelected(false);
			queryClient.invalidateQueries([
				"blueCoursesObjectsByDeptAndTerm",
				selectedDept.deptId,
				terms.termNumber,
			]);
			queryClient.invalidateQueries([
				"classIdObjectsByDeptAndTerm",
				selectedDept.deptId,
				terms.termNumber,
			]);
			setSelectionModel(createEmptyRowSelectionModel());
		}
		setDisabledUI(false);
		setHasCombinedCourse(false);
	};

	const buildSelectedClassesList = () => {
		const selectedClassesList = [];

		selectedBulkClassIds.forEach((classid) => {
			const selectedClassItem = effectiveRowsById.get(classid);
			if (!selectedClassItem) {
				return;
			}

			const classToSubmit = {
				...selectedClassItem,
				roles: [...(selectedClassItem.roles ?? [])],
			};

			if (submitTAOnly) {
				classToSubmit.roles = classToSubmit.roles.filter((role) => role.class_role === "TA");
			}
			selectedClassesList.push(classToSubmit);
		});

		return selectedClassesList;
	};

	const handleSubmitAllSelected = async (options = {}) => {
		const skipMixedRoleCheck = Boolean(options?.skipMixedRoleCheck);
		const selectedClassesList = buildSelectedClassesList();
		const mixedRoleCourses = getMixedTARoleCourses(selectedClassesList);

		if (!skipMixedRoleCheck && mixedRoleCourses.length > 0) {
			setMixedRoleSubmitDialog({
				open: true,
				source: "selected",
				courses: mixedRoleCourses,
			});
			return;
		}

		setSubmitAllLoading(true);
		await postAllSuitableClassesMutation.mutateAsync({
			token,
			classesToSubmit: selectedClassesList,
			ccid,
		});
		if (downloadReceipt) {
			generateReceiptPdf(selectedClassesList);
		}
		setDisabledUI(false);
	};

	const handleCancelSubmitAllSuitable = () => {
		setReviewingClasses(false);
		setRows(prevRowsState);
	};

	const handleConfirmSubmitAllSuitable = async (options = {}) => {
		const skipMixedRoleCheck = Boolean(options?.skipMixedRoleCheck);
		const mixedRoleCourses = getMixedTARoleCourses(listOfSuitableCourses);

		if (!skipMixedRoleCheck && mixedRoleCourses.length > 0) {
			setMixedRoleSubmitDialog({
				open: true,
				source: "suitable",
				courses: mixedRoleCourses,
			});
			return;
		}

		setSubmitAllLoading(true);
		await postAllSuitableClassesMutation.mutateAsync({
			token,
			classesToSubmit: listOfSuitableCourses,
			ccid,
		});
		if (downloadReceipt) {
			generateReceiptPdf(listOfSuitableCourses);
		}
	};

	const handleSaveAttempt = (event) => {
		const { message, shouldPrevent } = validateClass(selectedClass);
		if (shouldPrevent) {
			event.preventDefault();
			setSnackbarMessage(message);
			setSnackbarOpen(true);
		}
	};

	const getClassValidationIssues = (selectedClassToValidate) => {
		const hasNoRoles = !selectedClassToValidate?.roles || selectedClassToValidate.roles?.length < 1;
		const hasNoBlockId =
			!selectedClassToValidate?.form || selectedClassToValidate.form?.length === 0;
		const isClassSizeTooSmall = selectedClassToValidate?.csize < 4;
		const hasMissingSurveyDates =
			!selectedClassToValidate?.survey_start || !selectedClassToValidate?.survey_end;
		const isSurveyStartAfterEnd =
			!hasMissingSurveyDates &&
			selectedClassToValidate?.survey_start >= selectedClassToValidate?.survey_end;
		const surveyEndConflictsWithExam =
			selectedClassToValidate?.survey_end &&
			selectedClassToValidate?.examdate &&
			dayjs(selectedClassToValidate.survey_end).isValid() &&
			dayjs(selectedClassToValidate.examdate).isValid() &&
			!dayjs(selectedClassToValidate.survey_end).isBefore(
				dayjs(selectedClassToValidate.examdate),
				"day",
			);
		const chosenBlockId = blockIdsByDept?.find(
			(blockId) => blockId.blkid === selectedClassToValidate?.form,
		);
		const roleOfBlockId = chosenBlockId?.inst_role;
		const roleList = Array.isArray(selectedClassToValidate?.roles)
			? selectedClassToValidate.roles
			: [];
		const isWrongBlockIdRole =
			roleList.some((role) => role.class_role !== "TA") && roleOfBlockId === "TA";
		const shouldValidateSurveySubmission = selectedClassToValidate?.evaluate !== "No";
		const issues = [];

		if (hasNoRoles && shouldValidateSurveySubmission) {
			issues.push({
				message: "Course is missing an assigned instructor.",
				shouldPrevent: true,
			});
		}
		if (isClassSizeTooSmall && shouldValidateSurveySubmission) {
			issues.push({
				message: "Classes with less than 4 students must be combined.",
				shouldPrevent: true,
			});
		}
		if (isWrongBlockIdRole && !submitTAOnly) {
			issues.push({
				message: "Question block is meant for TA's only but there are non-TA instructors.",
				shouldPrevent: true,
			});
		}
		if (hasNoBlockId && shouldValidateSurveySubmission) {
			issues.push({
				message: "A question block is necessary to save a course.",
				shouldPrevent: true,
			});
		}
		if (isSurveyStartAfterEnd && shouldValidateSurveySubmission) {
			issues.push({
				message: "Survey start date must be before survey end date.",
				shouldPrevent: true,
			});
		}
		if (surveyEndConflictsWithExam && shouldValidateSurveySubmission) {
			issues.push({
				message: "Survey end date must be at least one day before the final exam date.",
				shouldPrevent: true,
			});
		}
		if (hasMissingSurveyDates && shouldValidateSurveySubmission) {
			issues.push({
				message: "Survey start and end dates must be set.",
				shouldPrevent: true,
			});
		}

		return issues;
	};

	const validateClass = (selectedClassToValidate) => {
		const issues = getClassValidationIssues(selectedClassToValidate);
		if (issues.length > 0) {
			return {
				...issues[0],
				issues,
			};
		}
		return { message: "", shouldPrevent: false, issues: [] };
	};

	const validateClassList = (classList) => {
		const errors = new Map();
		classList.forEach((cls) => {
			getClassValidationIssues(cls).forEach((issue) => {
				if (!issue.shouldPrevent || !issue.message) {
					return;
				}

				const existingError = errors.get(issue.message) || {
					message: issue.message,
					classIds: [],
				};
				existingError.classIds.push(cls.classid);
				errors.set(issue.message, existingError);
			});
		});
		if (errors.size > 0) {
			const issues = Array.from(errors.values());
			return {
				message: issues
					.map((issue) => `${issue.message} Affected classes: ${issue.classIds.join(", ")}.`)
					.join("\n"),
				shouldPrevent: true,
				issues,
			};
		}
		return { message: "", shouldPrevent: false, issues: [] };
	};

	const handleOpenAddInstancedClass = () => {
		setOpenAddInstancedClass(!openAddInstancedClass);
	};

	const handleOpenCombineDialogFromModal = useCallback(
		(classesToCombine) => {
			if (!selectedClass || !classesToCombine?.length) {
				return;
			}

			const nextCombiningClasses = [selectedClass, ...classesToCombine].filter(
				(course, index, array) =>
					array.findIndex((candidate) => candidate.classid === course.classid) === index,
			);

			setCombiningClassesArray(nextCombiningClasses);
			setOpenClassAdjustCombineDialog(true);
		},
		[selectedClass, setCombiningClassesArray],
	);

	const dateFormats = ["DD/MM/YY", "YYYY-MM-DD", "DD/MMM/YYYY"];

	const handleOpenBlockPDF = () => {
		displayBlockPDF({
			blockIdsByDept,
			allBlockItems,
			departmentName: selectedDept.department,
			termNumber: terms.termNumber,
		});
	};

	const handleCloseSelectedClassModal = () => {
		if (unsavedChanges) {
			const confirmClose = window.confirm(
				"You have unsaved changes. Are you sure you want to close without saving?",
			);
			if (!confirmClose) {
				return;
			}
		}
		setTabValue(0);
		setCombiningClassesArray([]);
		setSelectedClass(null);
		setOpenSelectedClassModal(false);
	};

	const handleCloseMixedRoleSubmitDialog = () => {
		if (submitAllLoading) {
			return;
		}

		setMixedRoleSubmitDialog({
			open: false,
			source: null,
			courses: [],
		});
	};

	const handleContinueMixedRoleSubmit = async () => {
		const source = mixedRoleSubmitDialog.source;

		setMixedRoleSubmitDialog({
			open: false,
			source: null,
			courses: [],
		});

		if (source === "single") {
			await handleSaveChanges({ skipMixedRoleCheck: true });
			return;
		}

		if (source === "selected") {
			await handleSubmitAllSelected({ skipMixedRoleCheck: true });
			return;
		}

		if (source === "suitable") {
			await handleConfirmSubmitAllSuitable({ skipMixedRoleCheck: true });
		}
	};

	const handleRemoveRoleFromMixedRoleDialog = (roleId) => {
		applyRoleRemovalToCourses(mixedRoleSubmitDialog.courses, roleId);
		handleCloseMixedRoleSubmitDialog();
		setSubmitAllLoading(false);
	};

	const showBulkPanel = reviewingClasses || confirmSubmitAllSelected || hasBulkSelection;
	const tableRows = useMemo(() => {
		if (
			hasBulkSelection &&
			!reviewingClasses &&
			(openBulkEdit || confirmSubmitAllSelected)
		) {
			return effectiveRows.filter((row) => selectedBulkClassIds.includes(row.classid));
		}

		return filteredDisplayRows;
	}, [
		confirmSubmitAllSelected,
		effectiveRows,
		filteredDisplayRows,
		hasBulkSelection,
		openBulkEdit,
		reviewingClasses,
		selectedBulkClassIds,
	]);
	const tableFilterKey = `${filterKey}-${openBulkEdit ? "bulk-edit" : "browse"}-${
		confirmSubmitAllSelected ? "confirm" : "normal"
	}-${reviewingClasses ? "review" : "active"}`;
	const isTableFilterLocked = openBulkEdit || reviewingClasses || confirmSubmitAllSelected;
	const hasExternalTableFilters = Boolean(searchTerm) || courseStatusFilter !== "All";
	const isCourseStatusFilterDisabled =
		disabledUI || unsavedChanges || isTableFilterLocked;

	return (
		<div className="flex h-[calc(100dvh-0.75rem)] max-h-[calc(100dvh-0.75rem)] min-h-0 w-full overflow-hidden pb-3 text-zinc-900">
			<SelectedClassModal
				open={openSelectedClassModal}
				setOpen={setOpenSelectedClassModal}
				selectedClass={selectedClass}
				setSelectedClass={setSelectedClass}
				handleOpenAddInstancedClass={handleOpenAddInstancedClass}
				handleSaveAttempt={handleSaveAttempt}
				handleSaveChanges={handleSaveChanges}
				validateClass={validateClass}
				isTermActive={isTermActive}
				isAdmin={isAdmin}
				handleOpenCombineDialogFromModal={handleOpenCombineDialogFromModal}
				setUndoDialogOpen={setUndoDialogOpen}
				setOpenEvaluationDialog={setOpenEvaluationDialog}
				setClassForEvaluatedDialog={setClassForEvaluatedDialog}
				setEvaluationDialogState={setEvaluationDialogState}
				roles={roles}
				handleChangeRole={handleChangeRole}
				deleteInstructor={deleteInstructor}
				addInstructor={addInstructor}
				setAddInstructor={setAddInstructor}
				rows={rows}
				setRows={setRows}
				updateRow={updateRow}
				allBlockItemsTA={allBlockItemsTA}
				handleChangeBlkId={handleChangeBlkId}
				unsavedChanges={unsavedChanges}
				setUnsavedChanges={setUnsavedChanges}
				handleDateChangeEndDate={handleDateChangeEndDate}
				handleDateChangeExamDate={handleDateChangeExamDate}
				handleDateChangeSurveyStart={handleDateChangeSurveyStart}
				handleDateChangeSurveyEnd={handleDateChangeSurveyEnd}
				handleToggleExclude={handleToggleExclude}
				tabValue={tabValue}
				setTabValue={setTabValue}
				onClose={handleCloseSelectedClassModal}
				allBlockItems={allBlockItems}
			/>

			<MixedRoleSubmitDialog
				open={mixedRoleSubmitDialog.open}
				courses={mixedRoleSubmitDialog.courses}
				onCancel={handleCloseMixedRoleSubmitDialog}
				onContinue={handleContinueMixedRoleSubmit}
				onRemoveRole={handleRemoveRoleFromMixedRoleDialog}
				isSubmitting={submitAllLoading}
			/>

			<PostBatchSubmitDialog
				open={openPostBatchSubmitDialog}
				setOpen={setOpenPostBatchSubmitDialog}
				batchSubmitResponse={batchSubmitResponse}
			/>
			<InstancedClassAdjustModal
				open={openAddInstancedClass}
				setOpen={handleOpenAddInstancedClass}
				classAdjustRows={rows}
				deptId={selectedDept.deptId}
				handleRefresh={handleRefresh}
			/>
			<ConfirmCancelDialog
				rows={rows}
				setRows={setRows}
				open={openConfirmCancelDialog}
				setOpen={setOpenConfirmCancelDialog}
				setBatchUpdated={setBatchUpdated}
				setConfirmSubmitAllSelected={setConfirmSubmitAllSelected}
			/>

			<SubmitAllSuitableCoursesDialog
				open={openSubmitAllSuitableCoursesDialog}
				setOpen={setOpenSubmitAllSuitableCoursesDialog}
				listOfSuitableCourses={listOfSuitableCourses}
				postAllSuitableClassesMutation={postAllSuitableClassesMutation}
				ccid={ccid}
				renderInstructorNameTable={renderInstructorNameTable}
				submitAllLoading={submitAllLoading}
				setSubmitAllLoading={setSubmitAllLoading}
				token={token}
			/>

			<ConfirmEvaluationDialog
				open={openEvaluationDialog}
				setOpen={setOpenEvaluationDialog}
				evaluationDialogState={evaluationDialogState}
				classForEvaluatedDialog={classForEvaluatedDialog}
				handleEvaluatedDialogConfirmation={handleEvaluatedDialogConfirmation}
			/>

			<ClassAdjustCombineDialog
				open={openClassAdjustCombineDialog}
				blockIdsByDept={blockIdsByDept}
				setOpen={setOpenClassAdjustCombineDialog}
				list={classesForCombining}
				setList={setClassesForCombining}
				rows={rows}
				setRows={setRows}
				teamTeachingAgree={teamTeachingAgree}
				unwantedCombination={unwantedCombination}
				setUnwantedCombination={setUnwantedCombination}
				setHasCombinedCourse={setHasCombinedCourse}
			/>

			<UndoCombineDialog
				open={undoDialogOpen}
				handleUndo={handleUndo}
				setOpen={setUndoDialogOpen}
			/>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={5000}
				onClose={handleCloseSnackbar}
				message={snackbarMessage}
			/>

			<div className="grid h-full min-h-0 w-full grid-cols-7 gap-4 pt-4">
				<div
					className={`flex h-full min-h-0 w-full flex-col ${
						showBulkPanel ? "col-span-5 xl:col-span-4" : "col-span-7"
					}`}
				>
					<div className="flex flex-col items-center gap-2 justify-between w-full border-[1px] h-30 border-gray-400/70 border-b-0 px-2 rounded rounded-bl-none rounded-b-none bg-white">
						<div className="flex items-center  gap-2 w-full pt-3  border-indigo-500">
							<ListSelect
								disabled={unsavedChanges || disabledUI}
								className="termSelect"
								list={terms?.terms ? terms.terms : []}
								label="Term"
								size="small"
								width="30%"
								marginBottom="0px"
								value={terms.termValue || ""}
								onChange={(e) => {
									const termNumber = e.target.value.match(/\(([^)]+)\)/)[1];
									setTerms({
										...terms,
										termNumber: Number(termNumber),
										termValue: e.target.value,
									});
								}}
							/>
							<ListSelect
								disabled={unsavedChanges || disabledUI}
								className="deptSelect"
								list={departmentsList}
								label="Department"
								size="small"
								marginBottom="0px"
								value={selectedDept.department || ""}
								onChange={handleDepartmentChange}
							/>

							<ListSelect
								className="dateFormatSelect "
								list={dateFormats}
								label="Date Format"
								width="25%"
								size="small"
								marginBottom="0px"
								value={dateFormat || ""}
								onChange={(e) => setDateFormat(e.target.value)}
							/>

							<Button
								onClick={handleRefresh}
								disabled={unsavedChanges || disabledUI || (!isTermActive && !isAdmin)}
								variant="outlined"
								sx={{ py: 0.75, minWidth: "120px" }}
							>
								REFRESH
								{loading ? (
									<CircularProgress size={20} sx={{ marginLeft: "9px" }} />
								) : (
									<RefreshIcon sx={{ marginLeft: "5px" }} />
								)}
							</Button>
						</div>
						<div className="flex items-center justify-between gap-2 h-10 w-full mb-2  border-purple-500">
							<div className="h-10 flex-1 relative border-[1px] border-gray-400/70 focus:outline-green-800 py-1 px-1 rounded text-black placeholder-gray-600">
								<label htmlFor="search" className="sr-only ">
									Search
								</label>
								<input
									id="search"
									type="text"
									disabled={disabledUI || openBulkEdit}
									placeholder="Search for a Class ID, Class Title, or Instructor Name"
									className="w-full h-8 px-2 focus:outline-none active:outline-none  focus:border-0 active:border-0"
									value={searchTerm}
									onChange={handleSearch}
								/>
								{searchTerm?.length > 0 && !openBulkEdit && (
									<button
										onClick={() => {
											applySearch("");
										}}
										className="absolute uppercase right-2 top-2.5 text-gray-700 hover:bg-gray-800 hover:text-white font-semibold bg-gray-300 rounded text-xs px-1"
									>
										clear
									</button>
								)}
							</div>
							<ButtonGroup
								variant="outlined"
								size="small"
								disabled={isCourseStatusFilterDisabled}
								aria-label="Filter courses by submission status"
								sx={{ width: "30%", minWidth: "320px", height: "40px" }}
							>
								{courseStatusFilters.map((filterLabel) => {
									const isActive = courseStatusFilter === filterLabel;

									return (
										<Button
											key={filterLabel}
											onClick={() => setCourseStatusFilter(filterLabel)}
											variant={isActive ? "contained" : "outlined"}
											sx={{
												flex: 1,
												fontSize: "0.75rem",
												fontWeight: 600,
												whiteSpace: "nowrap",
											}}
										>
											{filterLabel}
										</Button>
									);
								})}
							</ButtonGroup>

							<button
								onClick={handleOpenBlockPDF}
								disabled={!selectedDept.deptId || disabledUI}
								className="select-none border hover:border-black rounded w-[25%] border-gray-400/70  flex gap-1 items-center justify-center  py-1.5 text-md "
							>
								<span>Blocks</span>
								<div className="text-primary">
									<LaunchIcon />
								</div>
							</button>
							<FormControlLabel
								control={
									<Checkbox
										checked={downloadReceipt}
										disabled={(!isAdmin && !isTermActive) || disabledUI}
										sx={{
											m: 0,
											paddingY: "0.45rem",
											"&.MuiCheckbox-root": {
												color: !disabledUI && "#275d38",
											},
										}}
										onChange={() => {
											setDownloadReceipt(!downloadReceipt);
											localStorage.setItem("downloadReceipt", !downloadReceipt ? "Y" : "N");
										}}
									/>
								}
								sx={{
									m: 0,

									fontSize: "0.8rem",
									paddingY: 0,
									minWidth: "120px",
								}}
								label="Receipt"
								className="select-none inline-flex items-center border-[1px] text-sm border-gray-400/70 text-primary rounded px-2 hover:bg-gray-100"
							/>
						</div>
					</div>
					<DataGridTable
						setOpenSelectedClassModal={setOpenSelectedClassModal}
						rows={tableRows}
						loading={loading}
						getRowClassName={getRowClassName}
						filterKey={tableFilterKey}
						reviewingClasses={reviewingClasses}
						selectedBulkClassIds={selectedBulkClassIds}
						confirmSubmitAllSelected={confirmSubmitAllSelected}
						renderInstructorNameTable={renderInstructorNameTable}
						validateClass={validateClass}
						dateFormat={dateFormat}
						fetchedClassIdsLoading={fetchedClassIdsLoading}
						handleOpenAddInstancedClass={handleOpenAddInstancedClass}
						hasExternalFiltersApplied={hasExternalTableFilters}
						onClearExternalFilters={() => {
							applySearch("");
							setCourseStatusFilter("All");
						}}
					/>
				</div>

				<div className={`${showBulkPanel ? "col-span-2 h-full min-h-0 xl:col-span-3" : "hidden"}`}>
					<ClassPanel
						isTermActive={isTermActive}
						isAdmin={isAdmin}
						validateClassList={validateClassList}
						allBlockItemsTA={allBlockItemsTA}
						rows={rows}
						effectiveRows={effectiveRows}
						updateRowsBatch={updateRowsBatch}
						reviewingClasses={reviewingClasses}
						handleCancelSubmitAllSuitable={handleCancelSubmitAllSuitable}
						handleConfirmSubmitAllSuitable={handleConfirmSubmitAllSuitable}
						handleCancelSubmitAllSelected={handleCancelSubmitAllSelected}
						handleSubmitAllSelected={handleSubmitAllSelected}
						handleReviewAllSelected={handleReviewAllSelected}
						confirmSubmitAllSelected={confirmSubmitAllSelected}
						handleClearSelection={handleClearSelection}
						listOfSuitableCourses={listOfSuitableCourses}
						submitAllLoading={submitAllLoading}
						setBatchUpdated={setBatchUpdated}
						batchUpdated={batchUpdated}
						deleteClassMutation={deleteClassMutation}
						token={token}
						someAreTA={someAreTA}
						someNotTA={someNotTA}
						submitTAOnly={submitTAOnly}
						setSubmitTAOnly={setSubmitTAOnly}
						allCoursesNonLEC={allCoursesNonLEC}
						selectedBulkClassIds={selectedBulkClassIds}
						onBulkAddInstructorToSelection={handleBulkAddInstructorToSelection}
						onBulkRemoveInstructorFromSelection={handleBulkRemoveInstructorFromSelection}
						onBulkRemoveRoleFromSelection={handleBulkRemoveRoleFromSelection}
						openBulkEdit={openBulkEdit}
						setOpenBulkEdit={setOpenBulkEdit}
					/>
				</div>
			</div>
		</div>
	);
}
